/**
 * Dashboard Page
 * Main overview with price cards, alert/trade feeds, and CoinGlass flow data
 */

import { PriceCard } from '@/components/dashboard/price-card';
import { AlertFeed } from '@/components/dashboard/alert-feed';
import { TradeFeed } from '@/components/dashboard/trade-feed';
import { FlowStats } from '@/components/dashboard/flow-stats';
import { OIBreakdownChart } from '@/components/dashboard/oi-breakdown-chart';
import { TakerFlowChart } from '@/components/dashboard/taker-flow-chart';
import { WhalePositionsTable } from '@/components/dashboard/whale-positions';
import { PositioningPanel } from '@/components/dashboard/positioning-panel';
import { OptionsPanel } from '@/components/dashboard/options-panel';
import { ETFPremiumPanel } from '@/components/dashboard/etf-premium-panel';
import { useAlertStore, type Asset } from '@/stores/alert-store';

const ASSETS: Asset[] = ['BTC', 'ETH', 'SOL', 'HYPE', 'LIT'];

export function Dashboard() {
  const { selectedAsset, setSelectedAsset } = useAlertStore();

  const showOptions = selectedAsset === 'BTC' || selectedAsset === 'ETH';

  return (
    <div className="space-y-6">
      {/* Asset Price Cards */}
      <section>
        <h2 className="mb-4 text-lg font-semibold">Markets</h2>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-5">
          {ASSETS.map((asset) => (
            <PriceCard
              key={asset}
              asset={asset}
              selected={selectedAsset === asset}
              onClick={() => setSelectedAsset(asset)}
            />
          ))}
        </div>
      </section>

      {/* Alert + Trade Feeds */}
      <div className="grid gap-6 lg:grid-cols-2">
        <AlertFeed asset={selectedAsset} limit={30} />
        <TradeFeed asset={selectedAsset} limit={20} />
      </div>

      {/* Flow Overview */}
      <section>
        <h2 className="mb-4 text-lg font-semibold">
          Flow Overview — {selectedAsset}
        </h2>

        {/* Row 1: Stat cards */}
        <FlowStats asset={selectedAsset} />

        {/* Row 2: Charts */}
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <OIBreakdownChart asset={selectedAsset} />
          <TakerFlowChart asset={selectedAsset} />
        </div>

        {/* Row 3: Whale positions + Positioning */}
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <WhalePositionsTable asset={selectedAsset} />
          <PositioningPanel asset={selectedAsset} />
        </div>

        {/* Row 4: Options + ETF/Premium */}
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          {showOptions && <OptionsPanel asset={selectedAsset} />}
          <ETFPremiumPanel />
        </div>
      </section>
    </div>
  );
}
